import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { CalendarDialog } from "@/components/CalendarDialog";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/fullscale-logo.png";
import { cn } from "@/lib/utils";

const services = [
  { label: "SMART Websites", href: "/services/websites" },
  { label: "Google Business Profile & SEO", href: "/services/gbp-seo" },
  { label: "Free GBP Audit", href: "/free-gbp-audit" },
  { label: "Reputation & Reviews", href: "/services/reputation" },
  { label: "Lead Generation Ads", href: "/services/lead-generation" },
  { label: "Automation & AI Tools", href: "/services/automation" },
  { label: "Reactivation Campaigns", href: "/services/reactivation" },
];

const navLinks = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Services", href: "/services", hasDropdown: true },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Contact", href: "/contact" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsServicesOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "fixed top-0 left-0 right-0 z-[9999] transition-all duration-500",
          isScrolled
            ? "glass-header shadow-header"
            : "bg-background/40 backdrop-blur-sm"
        )}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[64px] lg:h-[76px]">
            {/* Logo */}
            <Link
              to="/"
              className="flex-shrink-0 transition-opacity duration-300 hover:opacity-80"
            >
              <img
                src={logo}
                alt="FullScale Marketing - Digital Marketing for Australian Trades & Service Businesses"
                className="h-9 sm:h-10 lg:h-[42px] w-auto object-contain"
                width={176}
                height={44}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
              {navLinks.map((link) =>
                link.hasDropdown ? (
                  <div
                    key={link.href}
                    ref={dropdownRef}
                    className="relative"
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <Link
                      to={link.href}
                      className={cn(
                        "nav-link py-2 text-[15px] font-medium transition-all duration-300 whitespace-nowrap inline-flex items-center gap-1",
                        isActive(link.href)
                          ? "text-primary"
                          : "text-foreground/70 hover:text-foreground"
                      )}
                    >
                      {link.label}
                      <ChevronDown
                        className={cn(
                          "w-3.5 h-3.5 transition-transform duration-200",
                          isServicesOpen && "rotate-180"
                        )}
                      />
                    </Link>

                    <AnimatePresence>
                      {isServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-[280px]"
                        >
                          <div className="bg-card rounded-xl border border-border/60 shadow-xl p-2 backdrop-blur-xl">
                            {services.map((service) => (
                              <Link
                                key={service.href}
                                to={service.href}
                                className={cn(
                                  "block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all duration-200",
                                  isActive(service.href)
                                    ? "text-primary bg-primary/10"
                                    : "text-foreground/80 hover:text-primary hover:bg-primary/5"
                                )}
                              >
                                {service.label}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={cn(
                      "nav-link py-2 text-[15px] font-medium transition-all duration-300 whitespace-nowrap",
                      isActive(link.href)
                        ? "text-primary"
                        : "text-foreground/70 hover:text-foreground"
                    )}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center">
              <CalendarDialog buttonVariant="default" buttonSize="header" />
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2.5 text-foreground hover:bg-muted rounded-xl transition-all duration-300"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <motion.div
                animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </motion.div>
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden fixed inset-0 top-[64px] z-[9998] bg-card backdrop-blur-xl overflow-hidden"
          >
            <div className="h-full flex flex-col px-6 py-8 overflow-y-auto">
              <nav className="flex flex-col gap-1 flex-1" aria-label="Mobile navigation">
                {navLinks.map((link, index) =>
                  link.hasDropdown ? (
                    <div key={link.href}>
                      <motion.button
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.3 }}
                        onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                        className={cn(
                          "w-full text-left text-lg font-medium px-5 py-4 rounded-xl transition-all duration-300 flex items-center justify-between",
                          isActive(link.href)
                            ? "text-primary bg-primary/10"
                            : "text-foreground/80 hover:text-primary hover:bg-primary/5"
                        )}
                      >
                        {link.label}
                        <ChevronDown
                          className={cn(
                            "w-4 h-4 transition-transform duration-200",
                            mobileServicesOpen && "rotate-180"
                          )}
                        />
                      </motion.button>
                      <AnimatePresence>
                        {mobileServicesOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="pl-6 py-1 flex flex-col gap-0.5">
                              <Link
                                to="/services"
                                className="text-base font-medium px-4 py-2.5 rounded-lg text-primary/80 hover:bg-primary/5"
                              >
                                All Services
                              </Link>
                              {services.map((service) => (
                                <Link
                                  key={service.href}
                                  to={service.href}
                                  className={cn(
                                    "text-base font-medium px-4 py-2.5 rounded-lg transition-all duration-200",
                                    isActive(service.href)
                                      ? "text-primary bg-primary/10"
                                      : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                                  )}
                                >
                                  {service.label}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <motion.div
                      key={link.href}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.3 }}
                    >
                      <Link
                        to={link.href}
                        className={cn(
                          "block text-left text-lg font-medium px-5 py-4 rounded-xl transition-all duration-300",
                          isActive(link.href)
                            ? "text-primary bg-primary/10"
                            : "text-foreground/80 hover:text-primary hover:bg-primary/5"
                        )}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  )
                )}
              </nav>

              {/* Mobile Menu CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="pt-6 border-t border-border/40"
              >
                <CalendarDialog
                  buttonVariant="hero"
                  buttonSize="xl"
                  buttonClassName="w-full justify-center h-14 text-lg rounded-xl"
                />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
